/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        Runway Gen-3 Alpha Video Generation Driver (Task 6.3.3)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import type {
  VideoGeneratorDriver,
  VideoProviderCode,
  GenerateVideoOptions,
  VideoGenerationResult,
  GeneratedVideo,
  VideoAspectRatio,
} from '../types';

/**
 * Standard model code mappings for Runway Gen-3 Alpha.
 */
export const RUNWAY_MODEL_CODES: Record<string, string> = {
  'runway-gen3': 'gen3a_turbo',
  'gen3': 'gen3a_turbo',
  'gen3a': 'gen3a_turbo',
  'gen3a_turbo': 'gen3a_turbo',
  'runway-gen3a-turbo': 'gen3a_turbo',
  'runway-gen3-alpha': 'gen3a',
  'runway': 'gen3a_turbo',
};

/**
 * Base credit cost for supported Runway models (for 5 seconds block).
 */
export const RUNWAY_MODEL_CREDITS: Record<string, number> = {
  'gen3a_turbo': 25,
  'gen3a': 35,
};

/**
 * Supported aspect ratios by Runway API (16:9, 9:16).
 */
export function resolveRunwayRatio(aspectRatio?: VideoAspectRatio): '16:9' | '9:16' {
  if (aspectRatio === '9:16') {
    return '9:16';
  }
  return '16:9';
}

/**
 * Calculates platform credits consumed by a Runway video generation.
 */
export function calculateRunwayCredits(model: string, durationSeconds = 5): number {
  const normalized = model.toLowerCase().trim();
  const internalModel = RUNWAY_MODEL_CODES[normalized] ?? 'gen3a_turbo';
  const baseRate = RUNWAY_MODEL_CREDITS[internalModel] ?? 25;
  const durationBlocks = Math.max(1, Math.ceil(durationSeconds / 5));
  return baseRate * durationBlocks;
}

/**
 * Video generator driver for Runway Gen-3 Alpha models.
 */
export class RunwayVideoDriver implements VideoGeneratorDriver {
  public readonly providerCode: VideoProviderCode = 'runway';
  private static readonly RUNWAY_API_VERSION = '2024-09-13';
  private static readonly RUNWAY_BASE_URL = 'https://api.dev.runwayml.com/v1';

  /**
   * Checks whether this driver supports the requested model.
   */
  public supportsModel(model: string): boolean {
    const normalized = model.toLowerCase().trim();
    if (Object.prototype.hasOwnProperty.call(RUNWAY_MODEL_CODES, normalized)) {
      return true;
    }
    return normalized.includes('runway') || normalized.includes('gen3');
  }

  /**
   * Calculates platform credits required for this video generation.
   */
  public calculateCreditCost(model: string, durationSeconds = 5): number {
    return calculateRunwayCredits(model, durationSeconds);
  }

  /**
   * Executes AI video generation asynchronously via Runway Developer REST API.
   */
  public async generateVideo(options: GenerateVideoOptions): Promise<VideoGenerationResult> {
    const startTime = Date.now();
    const normalizedModel = options.model.toLowerCase().trim();

    if (!options.apiKey || !options.apiKey.trim()) {
      throw new Error('Runway API key is required for video generation');
    }

    if (!options.prompt || !options.prompt.trim()) {
      throw new Error('Prompt is required for video generation');
    }

    const internalModel = RUNWAY_MODEL_CODES[normalizedModel] ?? 'gen3a_turbo';
    const isImageToVideo = Boolean(options.imageUrl && options.imageUrl.trim());
    const duration = options.durationSeconds && options.durationSeconds > 5 ? 10 : 5;
    const ratio = resolveRunwayRatio(options.aspectRatio);

    const authHeader = options.apiKey.startsWith('Bearer ')
      ? options.apiKey
      : `Bearer ${options.apiKey}`;

    const headers: Record<string, string> = {
      'Authorization': authHeader,
      'X-Runway-Version': RunwayVideoDriver.RUNWAY_API_VERSION,
      'Content-Type': 'application/json',
      'Accept': 'application/json',
      ...(options.gatewayOptions?.customHeaders || {}),
    };

    // Prepare payload for Runway API
    const requestPayload: Record<string, unknown> = {
      promptText: options.prompt.trim(),
      model: internalModel,
      duration,
      ratio,
      watermark: false,
      ...(isImageToVideo ? { promptImage: options.imageUrl } : {}),
      ...options.extraParams,
    };

    // Endpoint: /image_to_video handles both Image-to-Video and prompt-assisted creations
    const endpointPath = isImageToVideo ? 'image_to_video' : 'image_to_video';
    const submitUrl = `${RunwayVideoDriver.RUNWAY_BASE_URL}/${endpointPath}`;

    const submitResponse = await fetch(submitUrl, {
      method: 'POST',
      headers,
      body: JSON.stringify(requestPayload),
      signal: options.abortSignal,
    });

    if (!submitResponse.ok) {
      const errorText = await submitResponse.text().catch(() => '');
      throw new Error(
        `Runway video generation submission failed (${submitResponse.status}): ${errorText || submitResponse.statusText}`
      );
    }

    const submitData = (await submitResponse.json()) as Record<string, unknown>;
    const taskId = typeof submitData.id === 'string' ? submitData.id : undefined;

    if (!taskId) {
      // If direct output returned
      const directVideo = this.extractVideoFromData(submitData);
      if (directVideo) {
        return {
          video: directVideo,
          model: options.model,
          provider: 'runway',
          creditsUsed: this.calculateCreditCost(options.model, options.durationSeconds),
          executionTimeMs: Date.now() - startTime,
          metadata: submitData,
        };
      }
      throw new Error('Runway API did not return a valid task ID or video output');
    }

    // Polling task status
    const pollIntervalMs = options.pollIntervalMs ?? 1000;
    const maxPollAttempts = options.maxPollAttempts ?? 60;
    const taskStatusUrl = `${RunwayVideoDriver.RUNWAY_BASE_URL}/tasks/${taskId}`;

    let attempts = 0;
    let completedData: Record<string, unknown> | null = null;

    while (attempts < maxPollAttempts) {
      attempts++;

      if (pollIntervalMs > 0) {
        await new Promise((resolve) => setTimeout(resolve, pollIntervalMs));
      }

      if (options.abortSignal?.aborted) {
        // Best-effort cancel task on upstream
        await fetch(taskStatusUrl, {
          method: 'DELETE',
          headers,
        }).catch(() => {});

        throw new Error('Runway video generation polling was aborted');
      }

      const pollResponse = await fetch(taskStatusUrl, {
        method: 'GET',
        headers: {
          'Authorization': authHeader,
          'X-Runway-Version': RunwayVideoDriver.RUNWAY_API_VERSION,
          'Accept': 'application/json',
        },
        signal: options.abortSignal,
      });

      if (!pollResponse.ok) {
        continue;
      }

      const taskData = (await pollResponse.json()) as Record<string, unknown>;
      const status = typeof taskData.status === 'string' ? taskData.status.toUpperCase() : '';

      if (status === 'SUCCEEDED') {
        completedData = taskData;
        break;
      }

      if (status === 'FAILED') {
        const failureReason = typeof taskData.failure === 'string'
          ? taskData.failure
          : typeof taskData.failureCode === 'string'
          ? taskData.failureCode
          : JSON.stringify(taskData);
        throw new Error(`Runway video generation task failed: ${failureReason}`);
      }

      if (status === 'CANCELLED') {
        throw new Error('Runway video generation task was cancelled upstream');
      }
    }

    if (!completedData) {
      throw new Error(`Runway video generation timed out after ${attempts} polling attempts`);
    }

    const videoItem = this.extractVideoFromData(completedData);
    if (!videoItem) {
      throw new Error('Completed response from Runway did not contain a valid video output URL');
    }

    return {
      video: videoItem,
      model: options.model,
      provider: 'runway',
      creditsUsed: this.calculateCreditCost(options.model, options.durationSeconds),
      executionTimeMs: Date.now() - startTime,
      requestId: taskId,
      metadata: completedData,
    };
  }

  /**
   * Helper to parse video information from Runway API response.
   */
  private extractVideoFromData(data: Record<string, unknown>): GeneratedVideo | null {
    // Format: { output: ["https://.../video.mp4"] }
    if (Array.isArray(data.output) && data.output.length > 0) {
      const firstUrl = data.output[0];
      if (typeof firstUrl === 'string' && firstUrl.startsWith('http')) {
        return {
          url: firstUrl,
          contentType: 'video/mp4',
        };
      }
    }

    // Format: { video: { url: "..." } }
    if (data.video && typeof data.video === 'object') {
      const v = data.video as Record<string, unknown>;
      if (typeof v.url === 'string' && v.url.startsWith('http')) {
        return {
          url: v.url,
          contentType: 'video/mp4',
        };
      }
    }

    // Format: { url: "https://..." }
    if (typeof data.url === 'string' && data.url.startsWith('http')) {
      return {
        url: data.url,
        contentType: 'video/mp4',
      };
    }

    return null;
  }
}
